import type { FC, CSSProperties } from 'react'
import { useRef, useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import type { RundownCueOrderItem, Cue } from '@rundown-studio/types'
import CueItemTitle from '../../components/rundown/cue-item-title.tsx'
import {
  rundownAtom,
  collapseGroupAtom,
  isGroupCollapsedAtomFamily,
} from '../../store/rundown.store'
import { CuesGroupCollapseButton } from './ui/cues-group-collapse-button.tsx'
import { CuesGroupPlannedTimings } from './ui/cues-group-planned-timings.tsx'
import { isAtEnd, isAtStart } from '@/shared/lib/selection-caret'
import {
  keyboardNavigationEventBus,
  KeyboardNavigationEvent,
  KeyboardNavigationDirection,
  KeyboardNavigationInput,
} from '@/features/keyboard-navigation-handler'
import { ColumnType } from '@/entities/rundown'
import { CuesGroupActualTimingsWidget } from './ui/cues-group-actual-timings.widget'

type CuesGroupProps = {
  cue: Cue
  childrenCues: RundownCueOrderItem[]
  readonly?: boolean

  onUpdateCue?: (value?: string) => void | Promise<void>
  style?: CSSProperties
}

export const CuesGroup: FC<CuesGroupProps> = ({
  cue,
  childrenCues,
  readonly,
  onUpdateCue,
  style,
}) => {
  const rundown = useAtomValue(rundownAtom)
  const collapseGroup = useSetAtom(collapseGroupAtom)
  // Read our own collapsed flag here instead of taking it as a prop from
  // CueItem. That keeps CueItem from subscribing to the collapsed-groups
  // array (which would re-render every cue in the rundown on every toggle).
  const collapsed = useAtomValue(isGroupCollapsedAtomFamily(cue.id))

  const titleRef = useRef(null)

  const handleGroupTitleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const el = titleRef.current as HTMLDivElement | null
      const atStart = el ? isAtStart(el) : false
      const atEnd = el ? isAtEnd(el) || el.innerText.trim().length === 0 : false

      const prevent = () => {
        event.preventDefault()
        event.stopPropagation()
      }

      // Tab handling maps to FORWARD/REVERSE (distinct from arrow keys)
      if (event.key === 'Tab') {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: event.shiftKey
            ? KeyboardNavigationDirection.REVERSE
            : KeyboardNavigationDirection.FORWARD,
          fromCueId: cue.id,
          fromColumnId: ColumnType.HEADING_COLUMN,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }

      if (event.key === 'ArrowDown' && atEnd) {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: KeyboardNavigationDirection.DOWN,
          fromCueId: cue.id,
          fromColumnId: ColumnType.HEADING_COLUMN,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }

      if (event.key === 'ArrowUp' && atStart) {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: KeyboardNavigationDirection.UP,
          fromCueId: cue.id,
          fromColumnId: ColumnType.HEADING_COLUMN,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }

      if (event.key === 'ArrowRight' && atEnd) {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: KeyboardNavigationDirection.RIGHT,
          fromCueId: cue.id,
          fromColumnId: ColumnType.HEADING_COLUMN,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }
    },
    [cue.id],
  )

  return (
    <div
      className="relative overflow-hidden flex flex-row justify-between gap-0 w-[558px]"
      style={style}
    >
      <div className="w-full h-full flex flex-col justify-between">
        <div className="flex flex-col h-full justify-center">
          <div>
            <CueItemTitle
              value={cue.title}
              inputRef={titleRef}
              className="font-semibold px-4"
              onChange={onUpdateCue}
              readonly={readonly || cue.settings?.preventEdits}
              onKeyDown={handleGroupTitleKeyDown}
            />
          </div>

          <CuesGroupPlannedTimings
            childrenCues={childrenCues}
            timezone={rundown?.timezone}
          />
        </div>
        <CuesGroupActualTimingsWidget childrenCues={childrenCues} />
      </div>
      <div className="absolute top-1/2 -translate-y-1/2 right-0 shrink-0 flex flex-col justify-center mr-2">
        <CuesGroupCollapseButton
          onClick={() =>
            collapseGroup({
              cueId: cue.id,
            })
          }
          childrenCount={childrenCues.length}
          collapsed={collapsed}
        />
      </div>
    </div>
  )
}
